import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { navigate } from 'gatsby'
import { motion, AnimatePresence } from 'framer-motion'
import Scrollbar from 'smooth-scrollbar'
import FocusTrap from 'focus-trap-react'

import { css } from '@emotion/core'
import tw from 'twin.macro'

import { useStore } from '../store/index'

import PanelHeader from './panel-header'
import Button from './button'

const variants = {
  hide: { opacity: 0 },
  show: { opacity: 1 },
}

const ArticleContainer = ({ heading, ctaText, ctaUrl, children }) => {
  // use this ref to store the dom element, to destroy the scrollbar instance on close
  const [isExiting, setIsExiting] = useState(false)
  const ref = useRef()
  const setTriggerWorldZoomOut = useStore(state => state.setTriggerWorldZoomOut)

  const scrollableElRef = useCallback(node => {
    if (node !== null) {
      Scrollbar.init(node, {
        delegateTo: document,
      })
      ref.current = node
    }
  }, [])

  const close = () => {
    setIsExiting(true)
    // we want to let the fade + zoom out animation finish playing before the annoying stutter
    setTimeout(() => {
      navigate('/')
    }, 700)
  }

  // set escape key handler
  useEffect(() => {
    const escFunction = e => {
      if (e.keyCode === 27) {
        close()
      }
    }
    document.addEventListener('keydown', escFunction, false)

    return () => {
      setTimeout(() => {
        Scrollbar.destroy(ref.current)
      }, 1000)
      document.removeEventListener('keydown', escFunction, false)
    }
  }, []) // eslint-disable-line

  return (
    <AnimatePresence>
      {!isExiting && (
        <FocusTrap>
          <motion.div
            tw="fixed inset-0 z-50 flex justify-center items-center"
            variants={variants}
            initial="hide"
            animate="show"
            exit="hide"
            transition={{ duration: 0.4, type: 'tween', ease: 'easeOut' }}
            css={css`
              .scrollbar-track {
                ${tw`bg-transparent!`}
                width: 12px !important;
              }
              .scrollbar-thumb {
                ${tw`bg-white! rounded-full!`}
                width: 12px !important;
              }
            `}
          >
            <div tw="absolute inset-0 bg-black opacity-50 z-40"></div>
            <div
              tw="text-white w-full max-w-5xl mx-4 h-full relative flex flex-col z-50 rounded overflow-hidden"
              css={css`
                height: 95%;

                @media (min-width: 768px) {
                  height: 70%;
                }

                background-color: rgba(0, 0, 0, 0.7);
              `}
            >
              <PanelHeader
                heading={heading}
                onClose={() => {
                  setTriggerWorldZoomOut(true)
                  close()
                }}
              />
              <div
                tw="relative z-40 pt-12 overflow-x-hidden w-full"
                css={css`
                  margin-top: 56px;
                `}
                ref={scrollableElRef}
              >
                {children}
              </div>
              {ctaText && ctaUrl ? (
                <div tw="absolute z-50 bottom-0 inset-x-0 pointer-events-none w-full flex flex-row justify-center pb-8">
                  <Button text={ctaText.toLowerCase()} href={ctaUrl} />
                </div>
              ) : null}
            </div>
          </motion.div>
        </FocusTrap>
      )}
    </AnimatePresence>
  )
}

ArticleContainer.propTypes = {
  heading: PropTypes.string,
  ctaText: PropTypes.string,
  ctaUrl: PropTypes.string,
  children: PropTypes.node,
}

ArticleContainer.defaultProps = {
  heading: null,
  ctaText: null,
  ctaUrl: null,
  children: null,
}

export default ArticleContainer
